import { useCallback, useEffect, useState } from 'react';
import { api, describeError } from '../api';
import { Json, Message, Pill, Section } from '../components/ui';
import { useSession } from '../session';

interface SecretRef { id: string; provider: string; label: string; fingerprint: string; active: boolean; created_at: string; last_used_at: string | null; use_count: number; last_error: string | null }
interface Profile {
  id: string;
  name: string;
  api?: string;
  model?: string;
  'api-url'?: string;
  preset?: string;
  'reasoning-template'?: string;
  'prompt-post-processing'?: string;
  is_active: boolean;
  extensions: { pockettavern?: { credential_source?: string; secret_id?: string | null; model_id?: string; context_length?: number } };
}
interface Model {
  id: string;
  display_name?: string;
  provider: string;
  source?: 'official' | 'user';
  route?: string;
  tier_min?: string;
  enabled?: boolean;
  available_for_me?: boolean;
  is_default?: boolean;
  priority?: number;
  context_length?: number;
  credits_per_1k?: number | null;
  credits_per_turn?: number | null;
  pricing?: { input: number; output: number };
  channel?: string;
  temperature?: number;
  custom_model_id?: string;
  model?: string;
}
interface CatalogProvider { id: string; display_name: string; adapter: string; base_url: string; models: Array<{ id: string; display_name: string }> }
interface Catalog { providers: CatalogProvider[] }
const POST_PROCESSING = ['', 'merge', 'semi', 'strict', 'single', 'merge_tools', 'semi_tools', 'strict_tools'];

export function ConnectionsPage() {
  const { env, me } = useSession();
  const [secrets, setSecrets] = useState<SecretRef[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [wizProvider, setWizProvider] = useState('deepseek');
  const [wizModel, setWizModel] = useState('deepseek-chat');
  const [wizKey, setWizKey] = useState('');
  const [wizTemp, setWizTemp] = useState('0.8');
  const [wizName, setWizName] = useState('');
  const [wizId, setWizId] = useState('');
  const [effective, setEffective] = useState<unknown>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [key, setKey] = useState('');
  const [provider, setProvider] = useState('openai-compatible');
  const [label, setLabel] = useState('');
  const [pname, setPname] = useState('My profile');
  const [source, setSource] = useState<'official' | 'byok' | 'local'>('official');
  const [secretId, setSecretId] = useState('');
  const [modelId, setModelId] = useState('gemini-2.5-flash');
  const [apiUrl, setApiUrl] = useState('');
  const [chatId, setChatId] = useState('');
  const [reasoningTemplate, setReasoningTemplate] = useState('');
  const [postProcessing, setPostProcessing] = useState('');
  const [contextLength, setContextLength] = useState('');
  const [reasoningPresets, setReasoningPresets] = useState<string[]>([]);

  const run = async (fn: () => Promise<string | void>) => {
    setErr(null);
    setMsg(null);
    try {
      const m = await fn();
      if (m) setMsg(m);
    } catch (e) {
      setErr(describeError(e));
    }
  };

  const load = useCallback(async () => {
    if (!me) return;
    const [s, p, m, r, cat] = await Promise.all([
      api<SecretRef[]>('/api/secrets'),
      api<Profile[]>('/api/connection-profiles'),
      api<Model[]>('/api/models'),
      api<Record<string, string[]>>('/api/presets').catch(() => ({ data: {} as Record<string, string[]> })),
      api<CatalogProvider[]>('/api/custom-models/providers').catch(() => ({ data: [] as CatalogProvider[] })),
    ]);
    setSecrets(s.data);
    setProfiles(p.data);
    setModels(m.data);
    setReasoningPresets(r.data.reasoning ?? []);
    setCatalog({ providers: cat.data });
  }, [me]);
  const officialModels = models.filter((m) => m.source !== 'user');
  const userModels = models.filter((m) => m.source === 'user');
  const wizProv = catalog?.providers.find((p) => p.id === wizProvider);
  const patchProfile = (p: Profile, patch: Record<string, unknown>) => void run(async () => { await api(`/api/connection-profiles/${p.id}`, { method: 'PATCH', body: patch }); await load(); return '档案已更新'; });
  const activateListed = (id: string) => void run(async () => { await api(`/api/models/${encodeURIComponent(id)}/activate`, { method: 'PUT' }); await load(); return '已设为默认模型'; });

  useEffect(() => {
    void run(load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, env]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <Section title="密钥金库" actions={<span className="text-[11px] text-zinc-500">明文只在创建/轮换时出现一次 · 需 features.byok</span>}>
          <Message text={err} kind="error" />
          <Message text={msg} kind="ok" />
          <div className="grid grid-cols-2 gap-2">
            <select className="input" value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="openai-compatible">openai-compatible</option>
              <option value="anthropic">anthropic</option>
              <option value="google-ai-studio">google-ai-studio</option>
            </select>
            <input className="input" placeholder="label" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <input className="input font-mono" type="password" placeholder="API key（不会回显、不会入库明文）" value={key} onChange={(e) => setKey(e.target.value)} />
          <button className="btn" disabled={!key} onClick={() => void run(async () => {
            await api('/api/secrets', { method: 'POST', body: { provider, key, label } });
            setKey('');
            await load();
            return '已创建（仅指纹可见）';
          })}>添加密钥</button>
          <ul className="divide-y divide-zinc-800 text-sm">
            {secrets.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-2 py-2">
                <span className="font-mono text-xs">{s.fingerprint}</span>
                <Pill>{s.provider}</Pill>
                {s.label && <span className="text-zinc-400">{s.label}</span>}
                {s.last_error && <Pill tone="rose">{s.last_error}</Pill>}
                <span className="ml-auto flex gap-1">
                  <button className="btn-ghost py-0 text-[11px]" onClick={() => void run(async () => { const { data } = await api<{ ok: boolean; error?: { reason?: string } }>(`/api/secrets/${s.id}/verify`, { method: 'POST' }); await load(); return data.ok ? 'verify ok' : `verify failed ${data.error?.reason ?? ''}`; })}>verify</button>
                  <button className="btn-ghost py-0 text-[11px]" onClick={() => { const next = prompt('新 key'); if (!next) return; void run(async () => { await api(`/api/secrets/${s.id}/rotate`, { method: 'POST', body: { key: next } }); await load(); return '已轮换'; }); }}>rotate</button>
                  <button className="btn-ghost py-0 text-[11px] text-rose-300" onClick={() => void run(async () => { await api(`/api/secrets/${s.id}`, { method: 'DELETE' }); await load(); return '已删除'; })}>删除</button>
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="连接档案">
          <input className="input" value={pname} onChange={(e) => setPname(e.target.value)} placeholder="名称" />
          <div className="grid grid-cols-3 gap-2">
            <select className="input" value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
              <option value="official">official（平台 key，扣积分）</option>
              <option value="byok">byok（上传 key，不扣积分）</option>
              <option value="local">local（客户端执行）</option>
            </select>
            <select className="input" value={modelId} onChange={(e) => setModelId(e.target.value)}>
              {officialModels.map((m) => <option key={m.id} value={m.id}>{m.display_name}{m.is_default ? '（默认）' : ''}{m.available_for_me === false ? '（当前档不可用）' : ''}</option>)}
              <option value="custom">自定义…</option>
            </select>
            <select className="input" value={secretId} onChange={(e) => setSecretId(e.target.value)} disabled={source !== 'byok'}>
              <option value="">（无密钥）</option>
              {secrets.map((s) => <option key={s.id} value={s.id}>{s.label || s.fingerprint}</option>)}
            </select>
          </div>
          {source !== 'official' && <input className="input font-mono text-xs" placeholder="自定义 api-url（https）" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} />}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <select className="input" value={reasoningTemplate} onChange={(e) => setReasoningTemplate(e.target.value)} title="reasoning-template：解析 <think> 段的 reasoning 预设">
              <option value="">reasoning-template（无）</option>
              {reasoningPresets.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <select className="input" value={postProcessing} onChange={(e) => setPostProcessing(e.target.value)} title="prompt-post-processing（ST mergeMessages）">
              {POST_PROCESSING.map((v) => <option key={v} value={v}>{v === '' ? 'post-processing（无）' : v}</option>)}
            </select>
            <input className="input" placeholder="context_length（local / byok）" value={contextLength} onChange={(e) => setContextLength(e.target.value)} disabled={source === 'official'} />
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn" onClick={() => void run(async () => {
              const pt: Record<string, unknown> = { credential_source: source, secret_id: secretId || null, model_id: modelId };
              if (source !== 'official' && contextLength) pt.context_length = Number(contextLength);
              await api('/api/connection-profiles', { method: 'POST', body: { name: pname, api: source === 'official' ? 'openai' : provider, 'api-url': apiUrl || undefined, model: modelId, ...(reasoningTemplate ? { 'reasoning-template': reasoningTemplate } : {}), ...(postProcessing ? { 'prompt-post-processing': postProcessing } : {}), extensions: { pockettavern: pt } } });
              await load();
              return '档案已创建';
            })}>创建档案</button>
            <button className="btn-ghost" onClick={() => void run(async () => {
              const { data } = await api<{ created: unknown[]; warnings: string[] }>('/api/connection-profiles/import-from-settings', { method: 'POST', body: {} });
              await load();
              return `已从 ST settings 导入 ${data.created.length}${data.warnings.length ? `（警告 ${data.warnings.length}）` : ''}`;
            })}>从 settings 导入</button>
          </div>
          <ul className="divide-y divide-zinc-800 text-sm">
            {profiles.map((p) => (
              <li key={p.id} className="py-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{p.name}</span>
                  <Pill tone={p.extensions.pockettavern?.credential_source === 'byok' ? 'indigo' : p.extensions.pockettavern?.credential_source === 'local' ? 'amber' : 'emerald'}>{p.extensions.pockettavern?.credential_source ?? 'official'}</Pill>
                  {p.is_active && <Pill tone="emerald">激活</Pill>}
                  <span className="font-mono text-[11px] text-zinc-500">{p.extensions.pockettavern?.model_id ?? p.model}</span>
                  <select className="input w-auto py-0 text-[11px]" value={p['reasoning-template'] ?? ''} onChange={(e) => patchProfile(p, { 'reasoning-template': e.target.value || undefined })} title="reasoning-template">
                    <option value="">think: 无</option>
                    {reasoningPresets.map((n) => <option key={n} value={n}>think: {n}</option>)}
                  </select>
                  <select className="input w-auto py-0 text-[11px]" value={p['prompt-post-processing'] ?? ''} onChange={(e) => patchProfile(p, { 'prompt-post-processing': e.target.value })} title="prompt-post-processing">
                    {POST_PROCESSING.map((v) => <option key={v} value={v}>pp: {v || '无'}</option>)}
                  </select>
                  <span className="ml-auto flex gap-1">
                    <button className="btn-ghost py-0 text-[11px]" onClick={() => void run(async () => { await api(`/api/connection-profiles/${p.id}/activate`, { method: 'PUT' }); await load(); return '已激活'; })}>激活</button>
                    <button className="btn-ghost py-0 text-[11px]" onClick={() => void run(async () => { const { data } = await api<{ ok: boolean; error?: { message?: string } }>(`/api/connection-profiles/${p.id}/test`, { method: 'POST', body: { list_models: true } }); return data.ok ? 'test ok' : `test failed ${data.error?.message ?? ''}`; })}>test</button>
                    <button className="btn-ghost py-0 text-[11px] text-rose-300" onClick={() => void run(async () => { await api(`/api/connection-profiles/${p.id}`, { method: 'DELETE' }); await load(); return '已删除'; })}>删除</button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      </div>

      <div className="space-y-4">
        <Section title="云端自定义模型（catalog-only · 需 features.byok）" actions={<span className="text-[11px] text-zinc-500">四步：供应商 → 模型 → API key → temperature</span>}>
          <div className="grid grid-cols-2 gap-2">
            <select className="input" disabled={!!wizId} value={wizProvider} onChange={(e) => {
              const id = e.target.value;
              setWizProvider(id);
              const first = catalog?.providers.find((p) => p.id === id)?.models[0]?.id;
              if (first) setWizModel(first);
            }}>
              {(catalog?.providers ?? []).map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
            <select className="input" value={wizModel} onChange={(e) => setWizModel(e.target.value)}>
              {(wizProv?.models ?? []).map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
            <input className="input font-mono" type="password" placeholder="API key（只出现一次）" value={wizKey} onChange={(e) => setWizKey(e.target.value)} />
            <input className="input" placeholder="temperature 0–2" value={wizTemp} onChange={(e) => setWizTemp(e.target.value)} />
          </div>
          {wizId && <p className="text-[11px] text-amber-200">正在编辑 {`custom:${wizId}`} · 不填 key 则保留原密钥</p>}
          <input className="input" placeholder="名称（缺省 = catalog display_name）" value={wizName} onChange={(e) => setWizName(e.target.value)} />
          <p className="text-[11px] text-zinc-500">base_url 固定 {wizProv?.base_url ?? '—'} · 新建 POST /api/custom-models；编辑 PATCH /api/custom-models/{'{id}'}。同一把 key / 同一 catalog 模型可建多条（名字可以相同，列表按 {'`custom:{uuid}`'} 区分）· Free POST 403 `AI_BYOK_REQUIRES_PLAN`</p>
          <div className="flex flex-wrap gap-2">
            <button className="btn" disabled={!wizId && !wizKey} onClick={() => void run(async () => {
              const body = {
                model: wizModel,
                temperature: Number(wizTemp),
                ...(wizName.trim() ? { name: wizName.trim() } : {}),
                ...(wizKey ? { api_key: wizKey } : {}),
              };
              if (wizId) await api(`/api/custom-models/${wizId}`, { method: 'PATCH', body });
              else await api('/api/custom-models', { method: 'POST', body: { ...body, api_key: wizKey } });
              setWizKey('');
              if (!wizId) setWizName('');
              await load();
              return wizId ? '自定义模型已更新' : '自定义模型已创建';
            })}>{wizId ? '保存自定义模型' : '创建自定义模型'}</button>
            {wizId && <button className="btn-ghost" onClick={() => { setWizId(''); setWizKey(''); setWizName(''); }}>取消编辑</button>}
          </div>
          <ul className="divide-y divide-zinc-800 text-sm">
            {userModels.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-2 py-2">
                <span className="font-medium">{m.display_name}</span>
                <Pill tone="indigo">source=user</Pill>
                <span className="font-mono text-[11px] text-zinc-500">{m.id}</span>
                <span className="font-mono text-[11px] text-zinc-500">{m.provider}/{m.model}</span>
                <span className="text-[11px] text-zinc-500">t={m.temperature}</span>
                {m.available_for_me ? <Pill tone="emerald">可用</Pill> : <Pill tone="rose">只读</Pill>}
                {m.is_default && <Pill tone="emerald">默认</Pill>}
                <span className="ml-auto flex gap-1">
                  <button className="btn-ghost py-0 text-[11px]" onClick={() => {
                    setWizId(m.custom_model_id ?? m.id.replace(/^custom:/i, ''));
                    setWizProvider(m.provider);
                    if (m.model) setWizModel(m.model);
                    setWizTemp(String(m.temperature ?? 0.8));
                    setWizName(m.display_name ?? '');
                    setWizKey('');
                  }}>编辑</button>
                  <button className="btn-ghost py-0 text-[11px]" disabled={m.is_default} onClick={() => activateListed(m.id)}>设为默认</button>
                  <button className="btn-ghost py-0 text-[11px] text-rose-300" onClick={() => void run(async () => { await api(`/api/custom-models/${m.custom_model_id}`, { method: 'DELETE' }); await load(); return '已删除'; })}>删除</button>
                </span>
              </li>
            ))}
          </ul>
        </Section>
        <Section title="模型注册表（当前档）">
          <p className="text-xs text-zinc-500">{'`GET /api/models` 官方项 `source=official`：现网 `charge_mode=fixed`，展示/结算用 `credits_per_turn`（钻石 / 轮）；`credits_per_1k` 是旧浮动单价，默认不用。`is_default` = 当前账号默认。自定义项 `credits_per_turn=null`，不扣钻石。'}</p>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500"><th className="py-1">模型</th><th>厂商</th><th>id</th><th>档位</th><th>钻石 / 轮</th><th>钻石 / 1K（闲）</th><th>USD / 1M（in / out）</th><th></th></tr></thead>
            <tbody>
              {officialModels.map((m) => (
                <tr key={m.id} className="border-b border-zinc-800/60">
                  <td className="py-1 font-medium text-zinc-200">{m.display_name}</td>
                  <td className="font-mono text-zinc-500">{m.provider}</td>
                  <td className="font-mono text-zinc-500">{m.id}</td>
                  <td>{m.tier_min}</td>
                  <td className="font-mono text-amber-200">{m.credits_per_turn ?? '—'}</td>
                  <td className="font-mono text-zinc-500">{m.credits_per_1k ?? '—'}</td>
                  <td className="font-mono text-zinc-500">{m.pricing ? `${m.pricing.input} / ${m.pricing.output}` : '—'}</td>
                  <td className="flex flex-wrap items-center gap-1">
                    {m.is_default && <Pill tone="emerald">默认</Pill>}
                    {m.available_for_me ? <Pill tone="emerald">可用</Pill> : <Pill tone="rose">不可用</Pill>}
                    <button className="btn-ghost py-0 text-[11px]" disabled={m.is_default || m.available_for_me === false} onClick={() => activateListed(m.id)}>设为默认</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
        <Section title="effective-config 查看器">
          <input className="input font-mono text-xs" placeholder="chat id（从聊天页复制）" value={chatId} onChange={(e) => setChatId(e.target.value)} />
          <button className="btn" disabled={!chatId} onClick={() => void run(async () => { const { data } = await api(`/api/chats/${chatId}/effective-config`); setEffective(data); return '已读取'; })}>读取</button>
          {effective !== null && <Json value={effective} className="max-h-[50vh]" />}
        </Section>
      </div>
    </div>
  );
}
